import { useEffect } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router';

import { ROUTE_SUPPLIERS_DASHBOARD } from '@/constants/router';
import { Audit as AuditComponent } from '@/features/audit';
import { PendingPage } from '@/features/pending-page';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useGetAuditCase } from '@/queries/get-audit-case';
import { useAuditStore } from '@/store/auditStore';

import type { AuditLoaderData } from './loader';

interface AuditWithDataProps {
  auditCaseErrors?: string[];
}

const AuditWithData = ({ auditCaseErrors }: AuditWithDataProps) => {
  const setAudit = useAuditStore((state) => state.setAudit);
  const setLastSavedAuditBtoa = useAuditStore((state) => state.setLastSavedAuditBtoa);

  const { audit, auditBtoa } = useLoaderData<AuditLoaderData>();

  useEffect(() => {
    setAudit(audit);
    setLastSavedAuditBtoa(auditBtoa);
  }, [audit, auditBtoa, setAudit, setLastSavedAuditBtoa]);

  return <AuditComponent auditCaseErrors={auditCaseErrors} />;
};

const AuditRoute = () => {
  const { audit_case_uuid: auditCaseUuid } = useParams();
  const navigate = useNavigate();
  const handleErrors = useErrorHandling();

  const { data: auditCase, error } = useGetAuditCase(auditCaseUuid || '');

  useEffect(() => {
    if (error) {
      handleErrors(error);
    }
  }, [error]);

  useEffect(() => {
    if (auditCase?.status === 'ACCEPTED') {
      navigate(ROUTE_SUPPLIERS_DASHBOARD(auditCase.supplierId));
    }
  }, [auditCase?.status, auditCase?.supplierId, navigate]);

  if (!auditCaseUuid || !auditCase) {
    return null;
  }

  const renderAuditScreen = () => {
    if (auditCase.status === 'PENDING') {
      return <PendingPage supplierId={auditCase.supplierId} />;
    }
    if (auditCase.status === 'ACCEPTED') {
      return null;
    }

    return <AuditWithData auditCaseErrors={auditCase.errors} />;
  };

  return (
    <>
      <title>Audit - AXPX Web App</title>
      <meta
        name="description"
        content="Fill out structured audit forms in the AXPX Web App to ensure accurate assessments and maintain a clear record of completed reviews."
      />
      {renderAuditScreen()}
    </>
  );
};

export { AuditRoute as Audit };
